import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Check, Star, ArrowRight, Menu, X, Linkedin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
                VM
              </div>
              <span className="text-xl font-bold text-slate-900">Vishal Mehta (Growth Marketer)</span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#about" className="text-slate-600 hover:text-slate-900 transition">About</a>
              <a href="#services" className="text-slate-600 hover:text-slate-900 transition">Services</a>
              <a href="#case-studies" className="text-slate-600 hover:text-slate-900 transition">Case Studies</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition">Testimonials</a>
              <Button 
                className="bg-blue-600 hover:bg-blue-700" 
                data-testid="header-book-consultation-btn"
                onClick={() => window.open('https://calendly.com/vmehta03/wavemaker', '_blank')}
              >
                Book Consultation
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col space-y-4">
              <a href="#about" className="text-slate-600 hover:text-slate-900 transition">About</a>
              <a href="#services" className="text-slate-600 hover:text-slate-900 transition">Services</a>
              <a href="#case-studies" className="text-slate-600 hover:text-slate-900 transition">Case Studies</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition">Testimonials</a>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4" data-testid="hero-section">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center justify-center mb-6 space-x-4">
            <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
              Top Rated Plus
            </span>
            <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
              100% Job Success
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-center text-slate-900 mb-6 leading-tight">
            Scale Your Web & App Revenue with<br />
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Data-Driven Growth Marketing
            </span>
          </h1>

          <p className="text-xl text-center text-slate-600 mb-12 max-w-3xl mx-auto" data-testid="hero-description">
            Expert in E-Com and App marketing specialist with 12+ years of experience driving user acquisition, 
            increasing retention, and maximizing ROAS for e-com and apps across Fintech, Retail, Beauty & Cosmetics, E-commerce, Food, Telecom and AI industries.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-lg px-8" 
              data-testid="book-consultation-btn"
              onClick={() => window.open('https://calendly.com/vmehta03/wavemaker', '_blank')}
            >
              Book Free Consultation
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8" data-testid="view-work-btn">
              View My Work <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-testid="stats-grid">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">50+</div>
                <div className="text-sm text-slate-600">Projects Completed</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">3.5x</div>
                <div className="text-sm text-slate-600">Average ROAS</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">$50 MN</div>
                <div className="text-sm text-slate-600">Managed Media Spends</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">+107%</div>
                <div className="text-sm text-slate-600">Orders Increase</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">-70%</div>
                <div className="text-sm text-slate-600">CPO Reduction</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">1M+</div>
                <div className="text-sm text-slate-600">App Installs</div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Stats - Removed duplicate "Projects Completed" */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-8">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-slate-900 mb-2">12+ Years</div>
                <div className="text-sm text-slate-600">Experience</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-slate-900 mb-2">100%</div>
                <div className="text-sm text-slate-600">Job Success Rate</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-blue-600 mb-2">Top Rated Plus</div>
                <div className="text-sm text-slate-600">Upwork Status</div>
              </CardContent>
            </Card>
          </div>

          {/* Authority Image */}
          <div className="mt-16 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_5732e08b-7d3d-4e32-a1fe-aa008b7cf2dd/artifacts/tgtqp5nf_Generated%20Image%20November%2029%2C%202025%20-%207_49PM.png"
              alt="Vishal Mehta - Marketing Expert"
              className="rounded-2xl shadow-2xl max-w-full h-auto"
              style={{ maxHeight: '500px', objectFit: 'contain' }}
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-slate-50" data-testid="about-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-4">About Me</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Driving Measurable Growth for Mobile Apps & E-Commerce
            </h3>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Expert Digital Marketer with over 10+ years of experience in App & Web E-commerce Performance media marketing, 
              media buying, strategic media planning, paid media, execution and optimization of integrated digital media marketing campaigns.
            </p>
            <p className="text-lg text-slate-700 font-semibold mt-4">
              Ex - WPP Media & Publicis Groupe
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card>
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-slate-900 mb-2">Strategic Planning</h4>
                <p className="text-slate-600">Data-driven growth strategies</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-slate-900 mb-2">User Acquisition</h4>
                <p className="text-slate-600">Quality installs & conversions</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-slate-900 mb-2">Performance Analytics</h4>
                <p className="text-slate-600">Advanced attribution & tracking</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-slate-900 mb-2">ROAS Optimization</h4>
                <p className="text-slate-600">Maximize return on ad spend</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <p className="text-lg text-slate-700 mb-6">
              12+ years of experience leading growth strategies across B2C verticals, managing monthly marketing budgets 
              in 6 figures in industries such as Fintech, Food, AI, E-Com and Subscription Apps.
            </p>
            <p className="text-lg text-slate-700 mb-6">
              Proven track record of developing and executing multi-channel growth strategies, driving measurable results 
              for Ecom & App.
            </p>
            <p className="text-lg text-slate-700 mb-6">
              Expertise in optimizing key KPIs including Customer Acquisition Cost (CAC), Lifetime Value (LTV), user growth, 
              ROAS and engagement to deliver sustainable business outcomes.
            </p>
            <p className="text-lg text-slate-700">
              Hands-on experience on closely working with client and tech to implement advance measurement, S2S, CAPI and 
              attribution frameworks including SKAN and Mobile Measurement Partners (MMPs) like Singular and AppsFlyer.
            </p>
          </div>

          {/* Clientele Section */}
          <div className="mt-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
            <h4 className="text-2xl font-bold text-center text-slate-900 mb-8">Worked on Leading Brands</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Kotak Mahindra Bank</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Tata Real Estate</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Sony Entertainment</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Enamor</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Deconstruct</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">BookMyChef</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Pizza Hut</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Orange Telecom</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Royal Jordanian Airline</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="font-semibold text-slate-800">Bank of Jordan</p>
              </div>
            </div>
          </div>

          {/* Skills Grid */}
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Paid Media Expertise</h4>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Google Ads (UAC, Search, Display)</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Meta Ads (Facebook & Instagram)</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Apple Search Ads (ASA)</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> TikTok Ads</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Snapchat Ads</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Twitter Ads</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Programmatic Advertising</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Analytics & Measurement</h4>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Google Analytics (GA4)</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Firebase Analytics</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Mobile Measurement Partners (MMPs)</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Attribution Modeling</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> S2S & CAPI Integration</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Data Modeling & SDK Events</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h4 className="text-xl font-bold text-slate-900 mb-4">Strategy & Optimization</h4>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> User Acquisition Strategy</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Retention Marketing</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> CAC:LTV Optimization</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> ROAS Improvement</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> A/B Testing</li>
                <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-600" /> Campaign Management</li>
              </ul>
            </div>
          </div>

          {/* Industries Served */}
          <div className="mt-12 text-center">
            <h4 className="text-xl font-bold text-slate-900 mb-6">Industries Served</h4>
            <div className="flex flex-wrap justify-center gap-3">
              {['Fintech', 'Retail', 'Beauty & Cosmetics', 'E-Commerce', 'Food & Beverage', 'Telecom', 'AI & Technology'].map((industry) => (
                <span key={industry} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                  {industry}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 bg-white" data-testid="services-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-4">Services</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              How I Can Help Grow Your E-com & App
            </h3>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive E-com & app marketing services designed to drive user acquisition, maximize retention, and scale revenue profitably.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">App User Acquisition</h4>
                <p className="text-slate-600 mb-6">
                  Drive quality installs and in-app conversions through data-driven campaigns across Google UAC, Meta, ASA, and more.
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Multi-channel campaign strategy</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Install optimization</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> In-app event tracking</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Performance monitoring</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Performance Marketing</h4>
                <p className="text-slate-600 mb-6">
                  Maximize ROI with strategic paid media campaigns focused on measurable results and continuous optimization.
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> ROAS optimization</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Budget allocation strategy</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Creative testing</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Real-time reporting</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Analytics & Attribution</h4>
                <p className="text-slate-600 mb-6">
                  Implement advanced measurement frameworks to track user journeys and optimize conversion funnels.
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> SDK integration (Firebase, Singular, AppsFlyer)</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Funnel analysis</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Cohort segmentation</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Attribution modeling</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-xl flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-slate-900 mb-4">Retention & Growth</h4>
                <p className="text-slate-600 mb-6">
                  Increase user lifetime value through strategic retention campaigns and engagement optimization.
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Retention marketing strategy</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Push notification campaigns</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> Email marketing automation</li>
                  <li className="flex items-start"><Check className="w-5 h-5 mr-2 text-green-600 flex-shrink-0 mt-0.5" /> CAC:LTV improvement</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 text-center text-white">
            <h3 className="text-3xl font-bold mb-4">Ready to scale your app & e-commerce?</h3>
            <p className="text-xl mb-8 opacity-90">
              Let's discuss your growth goals and create a customized strategy for your business.
            </p>
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-slate-100" 
              data-testid="schedule-consultation-btn"
              onClick={() => window.open('https://calendly.com/vmehta03/wavemaker', '_blank')}
            >
              Schedule Free Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-20 px-4 bg-slate-50" data-testid="case-studies-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-4">Case Studies</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Proven Results Across Industries
            </h3>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Real success stories from brands that trusted me to drive their app growth and revenue.
            </p>
          </div>

          <div className="space-y-8">
            {/* Enamor Case Study */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow" data-testid="case-study-enamor">
              <div className="md:flex">
                <div className="md:w-2/5">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_sales-scaling-hub/artifacts/9nvpjflf_Enamor_Case_Study%20%282%29.png"
                    alt="Enamor Case Study"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="md:w-3/5 p-8">
                  <div className="text-sm text-slate-600 mb-2">E-Commerce (Women's Innerwear & Lingerie)</div>
                  <h4 className="text-3xl font-bold text-slate-900 mb-4">
                    <a href="https://www.enamor.co.in/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition">
                      Enamor ↗
                    </a>
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Meta Ads</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Google Shopping</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Google Search</span>
                  </div>
                  <p className="text-lg text-slate-700 mb-6">
                    Increased online E-Commerce store revenue by 35% with 3:1 ROAS
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold text-blue-600">35%</div>
                      <div className="text-sm text-slate-600">Revenue Increase</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600">3:1</div>
                      <div className="text-sm text-slate-600">ROAS</div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>

            {/* Deconstruct Case Study */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow" data-testid="case-study-deconstruct">
              <div className="md:flex">
                <div className="md:w-2/5">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_sales-scaling-hub/artifacts/01y3h4xu_Deconstruct_Case_Study.png"
                    alt="Deconstruct Case Study"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="md:w-3/5 p-8">
                  <div className="text-sm text-slate-600 mb-2">E-Commerce (Beauty and Skincare)</div>
                  <h4 className="text-3xl font-bold text-slate-900 mb-4">
                    <a href="https://thedeconstruct.in/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition">
                      Deconstruct ↗
                    </a>
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Meta ASC</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Meta Catalog Ads</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Google Shopping</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">+2</span>
                  </div>
                  <p className="text-lg text-slate-700 mb-6">
                    ROAS improved from 0.8 to 2.1 in 6 months
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold text-blue-600">162%</div>
                      <div className="text-sm text-slate-600">ROAS Improvement</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600">6 months</div>
                      <div className="text-sm text-slate-600">Time Frame</div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>

            {/* Jood (Orange Jordan) Case Study */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow" data-testid="case-study-jood">
              <div className="md:flex">
                <div className="md:w-2/5 bg-slate-100 flex items-center justify-center p-4">
                  <img 
                    src="https://play-lh.googleusercontent.com/owQ2Tz7c5qOZtQXykLh3wwx9wGoztN9ldB4yMmKNZf8XNXyd-sK6GR8keenFoRmEDzs=w2560-h1440"
                    alt="Jood (Orange Jordan) App Screenshot"
                    className="max-h-96 w-auto object-contain"
                  />
                </div>
                <CardContent className="md:w-3/5 p-8">
                  <div className="text-sm text-slate-600 mb-2">Telecom</div>
                  <h4 className="text-3xl font-bold text-slate-900 mb-4">
                    <a href="https://play.google.com/store/apps/details?id=com.orangejo.jood&hl=en" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition">
                      Jood (Orange Jordan) ↗
                    </a>
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Meta</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Google</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">ASA</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">+1</span>
                  </div>
                  <p className="text-lg text-slate-700 mb-6">
                    Acquired 1M+ installs with 250K active users, 107% order increase, and 70% CPO decrease
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold text-blue-600">1M+</div>
                      <div className="text-sm text-slate-600">Installs</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600">107%</div>
                      <div className="text-sm text-slate-600">Order Increase</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600">70%</div>
                      <div className="text-sm text-slate-600">CPO Decrease</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600">40%</div>
                      <div className="text-sm text-slate-600">Retention Rate</div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>

            {/* Pizza Hut Case Study */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow" data-testid="case-study-pizzahut">
              <div className="md:flex">
                <div className="md:w-2/5 bg-slate-100 flex items-center justify-center p-4">
                  <img 
                    src="https://play-lh.googleusercontent.com/A8a5LLmlBbQ77A_Q4oyEypleZtf8_Bel9Feb1ONjS19a0EODORjw9FSaaPDP0ZlmVAlM8-nw3VqmViJVassWOw=w2560-h1440"
                    alt="Pizza Hut App Screenshot"
                    className="max-h-96 w-auto object-contain"
                  />
                </div>
                <CardContent className="md:w-3/5 p-8">
                  <div className="text-sm text-slate-600 mb-2">Food & Beverages</div>
                  <h4 className="text-3xl font-bold text-slate-900 mb-4">
                    <a href="https://play.google.com/store/apps/details?id=com.phdv.universal&hl=en" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition">
                      Pizza Hut App (India) ↗
                    </a>
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Google UAC</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Meta Ads</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Affiliates</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">+1</span>
                  </div>
                  <p className="text-lg text-slate-700 mb-6">
                    Drive incremental orders by 17% in 1 month
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold text-blue-600">17%</div>
                      <div className="text-sm text-slate-600">Order Growth</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600">1 month</div>
                      <div className="text-sm text-slate-600">Time Frame</div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>

            {/* BookMyChef Case Study */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow" data-testid="case-study-bookmychef">
              <div className="md:flex">
                <div className="md:w-2/5 bg-slate-100 flex items-center justify-center p-4">
                  <img 
                    src="https://play-lh.googleusercontent.com/3NEoV0CC_iVxZGw29AZaaNqyKou-TrupCcqfFjKRAO6WTp-1MYE8MrtED9WUVFctTH6oAUHXkNefsoJCm4vVLA=w2560-h1440"
                    alt="BookMyChef App Screenshot"
                    className="max-h-96 w-auto object-contain"
                  />
                </div>
                <CardContent className="md:w-3/5 p-8">
                  <div className="text-sm text-slate-600 mb-2">Food & Hospitality</div>
                  <h4 className="text-3xl font-bold text-slate-900 mb-4">
                    <a href="https://play.google.com/store/apps/details?id=com.bookmychef.userapp&hl=en" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition">
                      BookMyChef ↗
                    </a>
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">Meta App Install</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Google UAC</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Apple Search Ads</span>
                  </div>
                  <p className="text-lg text-slate-700 mb-6">
                    Improved CAC:LTV ratio by 80%
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold text-blue-600">80%</div>
                      <div className="text-sm text-slate-600">CAC:LTV Improvement</div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>

            {/* AI Astrology App Case Study */}
            <Card className="overflow-hidden hover:shadow-xl transition-shadow" data-testid="case-study-ai-astrology">
              <div className="md:flex">
                <div className="md:w-2/5 bg-slate-100 flex items-center justify-center p-4">
                  <img 
                    src="https://play-lh.googleusercontent.com/CaueNMRQnpw9pGIA3R-bX1nAAZQdHCL14yCAazVqWNbKqkWdIJNcLIqdNt5aK-8J1zM=w2560-h1440"
                    alt="AI-Driven Astrology App Screenshot"
                    className="max-h-96 w-auto object-contain"
                  />
                </div>
                <CardContent className="md:w-3/5 p-8">
                  <div className="text-sm text-slate-600 mb-2">AI & Technology</div>
                  <h4 className="text-3xl font-bold text-slate-900 mb-4">
                    <a href="https://play.google.com/store/apps/details?id=ai.humanize.conversify&hl=en" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition">
                      AI-Driven Astrology App ↗
                    </a>
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">SDK Integration</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">Meta</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Google</span>
                  </div>
                  <p className="text-lg text-slate-700 mb-6">
                    Successfully integrated SDK for complete funnel tracking and improved app conversions across Meta and Google
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-3xl font-bold text-blue-600">5.0</div>
                      <div className="text-sm text-slate-600">Client Rating</div>
                    </div>
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 bg-white" data-testid="testimonials-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-4">Testimonials</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              What Clients Say About Working With Me
            </h3>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Don't just take my word for it - hear from clients who've experienced measurable growth.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <Card className="hover:shadow-xl transition-shadow" data-testid="testimonial-1">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6">
                  Vishal has been an incredible supporter of my app marketing journey. Even before I officially hired him, 
                  he offered free consulting that gave me valuable insights into building an app business. This gesture showed 
                  his genuine passion for helping others succeed. Once we started working together, he...
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-slate-900 mb-1">Google Universal App Campaign Specialist for Initial App Downloads</p>
                  <p className="text-sm text-slate-600 mb-2">Oct 28, 2024 - Jan 23, 2025</p>
                  <p className="text-xs text-slate-500">Source: Upwork</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">Collaborative</span>
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs">Solution Oriented</span>
                    <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs">Accountable for Outcomes</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className="hover:shadow-xl transition-shadow" data-testid="testimonial-2">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6">
                  Vishal M was instrumental in helping us understand and optimize our app metrics. His deep expertise and 
                  actionable insights made a significant impact on improving our app's performance and user engagement. 
                  Highly recommended!
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-slate-900 mb-1">App Metrics Consultant</p>
                  <p className="text-sm text-slate-600 mb-2">Oct 20, 2024 - Jan 10, 2025</p>
                  <p className="text-xs text-slate-500">Source: Upwork</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs">Solution Oriented</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className="hover:shadow-xl transition-shadow" data-testid="testimonial-3">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6">
                  Very great job done by vishal. Vishal have supported in SDK integration for my AI driven app to track the 
                  entire funnel journey of your app and helped in tracking the App conversions across Meta and Google. 
                  I Will highly recommend him.
                </p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-slate-900 mb-1">Performance Marketing Specialist - AI-Driven Astrology App</p>
                  <p className="text-sm text-slate-600 mb-2">Aug 26, 2024 - Oct 15, 2024</p>
                  <p className="text-xs text-slate-500">Source: Upwork</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs">Solution Oriented</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section with Accordion */}
      <section className="py-20 px-4 bg-slate-50" data-testid="faq-section">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-4">FAQ</h2>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h3>
            <p className="text-xl text-slate-600">
              Got questions? I've got answers. Find out more about how I work and what to expect.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold text-slate-900">What industries do you specialize in?</span>
              </AccordionTrigger>
              <AccordionContent className="text-slate-700 pt-2 pb-4">
                I specialize in mobile app marketing across diverse industries including Fintech, Food & Beverage, 
                E-Commerce, AI & Technology, Subscription Apps, and Telecom. My experience spans B2C verticals with 
                proven success in scaling user acquisition and revenue.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold text-slate-900">What's your typical project engagement?</span>
              </AccordionTrigger>
              <AccordionContent className="text-slate-700 pt-2 pb-4">
                I offer flexible engagement models from one-time consulting to ongoing retainers. Projects typically start 
                with a free consultation call where we discuss your goals, challenges, and determine the best approach. 
                Most clients see results within the first 30-60 days.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold text-slate-900">How do you measure success?</span>
              </AccordionTrigger>
              <AccordionContent className="text-slate-700 pt-2 pb-4">
                Success is measured through KPIs that matter to your business: ROAS, CAC:LTV ratio, user acquisition costs, 
                retention rates, and overall revenue growth. I provide transparent reporting with real-time dashboards and 
                regular performance reviews.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold text-slate-900">What platforms do you work with?</span>
              </AccordionTrigger>
              <AccordionContent className="text-slate-700 pt-2 pb-4">
                I have hands-on expertise with Google Ads (UAC, Search, Display, Shopping), Meta Ads (Facebook & Instagram), 
                Apple Search Ads, TikTok Ads, Snapchat Ads, Twitter Ads, and Programmatic platforms. I also work with MMPs 
                like Singular, AppsFlyer, and analytics platforms like Firebase and GA4.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white rounded-lg px-6 border-0 shadow-sm">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="text-lg font-semibold text-slate-900">Do you work with startups or only established brands?</span>
              </AccordionTrigger>
              <AccordionContent className="text-slate-700 pt-2 pb-4">
                I work with both! Whether you're a startup looking to validate your product-market fit or an established 
                brand scaling to new heights, I tailor strategies to your stage and budget. My clients range from early-stage 
                apps to major brands like Pizza Hut.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700" data-testid="final-cta-section">
        <div className="container mx-auto max-w-4xl text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Scale Your App & E-Commerce Revenue?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Let's discuss your growth goals. Book a free 30-minute consultation call to explore how I can help you 
            drive user acquisition, improve retention, and maximize ROAS.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <div className="flex items-center">
              <Check className="w-6 h-6 mr-2" />
              <span>Free 30-minute consultation</span>
            </div>
            <div className="flex items-center">
              <Check className="w-6 h-6 mr-2" />
              <span>Customized growth strategy</span>
            </div>
            <div className="flex items-center">
              <Check className="w-6 h-6 mr-2" />
              <span>No obligation, just insights</span>
            </div>
          </div>

          <Button 
            size="lg" 
            className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-12" 
            data-testid="final-book-consultation-btn"
            onClick={() => window.open('https://calendly.com/vmehta03/wavemaker', '_blank')}
          >
            Book Your Free Consultation
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold">
              VM
            </div>
            <span className="text-xl font-bold">Vishal Mehta (Growth Marketer)</span>
          </div>
          <p className="text-slate-400 mb-8">
            Expert in E-Com and App Marketing Specialist | 12+ Years Experience | 50 Projects Completed
          </p>
          
          {/* Social Links */}
          <div className="flex justify-center space-x-6 mb-8">
            <a 
              href="https://www.linkedin.com/in/vishal-mehta-acquisition-expert/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-slate-400 hover:text-white transition"
            >
              <Linkedin className="w-5 h-5" />
              <span>LinkedIn</span>
            </a>
            <a 
              href="https://x.com/FuturisticLion" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center space-x-2 text-slate-400 hover:text-white transition"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span>X (Twitter)</span>
            </a>
          </div>

          <div className="flex justify-center space-x-6 mb-8">
            <a href="#about" className="text-slate-400 hover:text-white transition">About</a>
            <a href="#services" className="text-slate-400 hover:text-white transition">Services</a>
            <a href="#case-studies" className="text-slate-400 hover:text-white transition">Case Studies</a>
            <a href="#testimonials" className="text-slate-400 hover:text-white transition">Testimonials</a>
          </div>
          <div className="border-t border-slate-800 pt-8 text-slate-500 text-sm">
            <p>© 2025 Vishal M. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
